// src/pages/MyPostsPage.js
// One place to see and manage everything a person has posted across
// Events, Services, Skill Share, Community (Notices + Meet-ups), and Deals.
// Events and Services link out to their existing full edit pages (already
// built, more fields than fit here). The simpler content types — Skill
// Share, Notices, Meet-ups, Deals — get inline expand-to-edit for title
// and description, which covers the real day-to-day need (fixing a typo,
// updating details) without duplicating four more full edit pages.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useAuth } from '../lib/AuthContext';
import { PageBack } from '../components/shared/PageBack';
import BottomNav from '../components/layout/BottomNav';
import {
  getOrganiserEvents, deleteEvent,
  getMyServices, deleteService,
  getMySkillListings, updateSkillListing, deleteSkillListing,
  getMyNotices, updateNotice, deleteNotice,
  getMyMeetLocals, updateMeetLocal, deleteMeetLocal,
  getMyDeals, updateDeal, deleteDeal,
} from '../lib/db';

const fmtDate = (date) => {
  if (!date) return '—';
  try {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return format(d, 'd MMM yyyy');
  } catch { return '—'; }
};

// One row, reused for Skill Share / Notices / Meet-ups / Deals — the four
// content types that share the same "title + description" edit shape.
const EditableRow = ({ item, typeLabel, onSave, onDelete, secondaryLine, extraFields }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title || '');
  const [description, setDescription] = useState(item.description || '');
  const [extraValues, setExtraValues] = useState(
    () => Object.fromEntries((extraFields || []).map(f => [f.key, item[f.key] || '']))
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const extraTrimmed = Object.fromEntries(
        Object.entries(extraValues).map(([k, v]) => [k, v.trim() || null])
      );
      await onSave(item.id, { title: title.trim(), description: description.trim(), ...extraTrimmed });
      toast.success('Updated');
      setEditing(false);
    } catch (err) {
      console.error(err);
      toast.error('Could not save — please try again');
    }
    setSaving(false);
  };

  return (
    <div style={{ background: 'var(--white)', borderRadius: 16, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <span style={{ background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>
          {typeLabel}
        </span>
      </div>

      {editing ? (
        <>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            aria-label="Title"
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', color: 'var(--ink)', marginBottom: 8, boxSizing: 'border-box' }}
          />
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            aria-label="Description"
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 13, fontFamily: 'Nunito', color: 'var(--ink)', resize: 'none', marginBottom: 8, boxSizing: 'border-box' }}
          />
          {(extraFields || []).map(f => (
            <input
              key={f.key}
              value={extraValues[f.key] || ''}
              onChange={e => setExtraValues(v => ({ ...v, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              aria-label={f.placeholder}
              style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 10, padding: 10, fontSize: 13, fontFamily: 'Nunito', color: 'var(--ink)', marginBottom: 8, boxSizing: 'border-box' }}
            />
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 1, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setTitle(item.title || ''); setDescription(item.description || ''); setExtraValues(Object.fromEntries((extraFields || []).map(f => [f.key, item[f.key] || '']))); }}
              style={{ flex: 1, background: 'var(--cream)', color: 'var(--muted)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{item.title}</div>
          {(extraFields || []).map(f => item[f.key] && (
            <div key={f.key} style={{ fontSize: 12, color: 'var(--sage-dark)', fontWeight: 600, marginBottom: 2 }}>{item[f.key]}</div>
          ))}
          {item.description && (
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.4, marginBottom: 8 }}>{item.description}</div>
          )}
          {secondaryLine && <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginBottom: 10 }}>{secondaryLine}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setEditing(true)}
              style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              Edit
            </button>
            <button onClick={() => onDelete(item.id, item.title)}
              style={{ background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const Section = ({ title, emoji, count, children }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 }}>
      {emoji} {title} {count > 0 && `(${count})`}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {children}
    </div>
  </div>
);

const MyPostsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [skills, setSkills] = useState([]);
  const [notices, setNotices] = useState([]);
  const [meetLocals, setMeetLocals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const [ev, sv, sk, no, ml, de] = await Promise.all([
      getOrganiserEvents(user.uid).catch(err => { console.error('getOrganiserEvents failed:', err); return []; }),
      getMyServices(user.uid).catch(err => { console.error('getMyServices failed:', err); return []; }),
      getMySkillListings(user.uid).catch(err => { console.error('getMySkillListings failed:', err); return []; }),
      getMyNotices(user.uid).catch(err => { console.error('getMyNotices failed:', err); return []; }),
      getMyMeetLocals(user.uid).catch(err => { console.error('getMyMeetLocals failed:', err); return []; }),
      getMyDeals(user.uid).catch(err => { console.error('getMyDeals failed:', err); return []; }),
    ]);
    setEvents(ev); setServices(sv); setSkills(sk); setNotices(no); setMeetLocals(ml); setDeals(de);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user?.uid]);

  const handleDeleteEvent = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await deleteEvent(id);
    toast.success('Event deleted');
    load();
  };
  const handleDeleteSkill = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await deleteSkillListing(id);
    toast.success('Removed');
    load();
  };
  const handleDeleteNotice = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await deleteNotice(id);
    toast.success('Removed');
    load();
  };
  const handleDeleteMeetLocal = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await deleteMeetLocal(id);
    toast.success('Removed');
    load();
  };
  const handleDeleteService = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await deleteService(id);
    toast.success('Deleted');
    load();
  };

  const handleDeleteDeal = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await deleteDeal(id);
    toast.success('Removed');
    load();
  };

  const totalCount = events.length + services.length + skills.length + notices.length + meetLocals.length + deals.length;

  if (!user) {
    return (
      <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
        <PageBack fallbackPath="/profile" />
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-title">Sign in to see your posts</div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 100 }}>
      <div style={{ padding: '52px 20px 20px' }}>
        <PageBack fallbackPath="/profile" />
        <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginTop: 12, marginBottom: 4 }}>My Posts</h1>
        <div style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 500 }}>
          Everything you've posted, in one place{totalCount > 0 ? ` — ${totalCount} total` : ''}
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>
        ) : totalCount === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">Nothing posted yet</div>
            <div className="empty-state-body">Anything you post — an event, a service, a Skill Share listing, a notice, a meet-up, or a deal — will show up here so you can edit or remove it any time.</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 14, scrollbarWidth: 'none' }}>
              {[
                { key: 'all',        label: 'All',            count: totalCount },
                { key: 'events',     label: '📅 Events',       count: events.length },
                { key: 'services',   label: '🏘️ Services',    count: services.length },
                { key: 'skills',     label: '✨ Skill Share',  count: skills.length },
                { key: 'notices',    label: '🤝 Noticeboard',  count: notices.length },
                { key: 'meetlocals', label: '🤝 Meet-ups',     count: meetLocals.length },
                { key: 'deals',      label: '🏷️ Deals',       count: deals.length },
              ].filter(f => f.key === 'all' || f.count > 0).map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  style={{
                    flexShrink: 0, padding: '8px 16px', borderRadius: 100, border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', whiteSpace: 'nowrap',
                    background: filter === f.key ? 'var(--sage-deep)' : 'var(--white)',
                    color: filter === f.key ? 'white' : 'var(--ink)',
                    boxShadow: filter === f.key ? 'none' : 'var(--shadow-sm)',
                  }}>
                  {f.label}{f.key !== 'all' ? ` (${f.count})` : ''}
                </button>
              ))}
            </div>

            {(filter === 'all' || filter === 'events') && events.length > 0 && (
              <Section title="Events" emoji="📅" count={events.length}>
                {events.map(ev => (
                  <div key={ev.id} style={{ background: 'var(--white)', borderRadius: 16, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{ev.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginBottom: 10 }}>
                      {fmtDate(ev.nextDate || ev.date)} · {ev.venue}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => navigate(`/organiser/edit/${ev.id}`)}
                        style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button onClick={() => handleDeleteEvent(ev.id, ev.title)}
                        style={{ background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {(filter === 'all' || filter === 'services') && services.length > 0 && (
              <Section title="Services" emoji="🏘️" count={services.length}>
                {services.map(sv => (
                  <div key={sv.id} style={{ background: 'var(--white)', borderRadius: 16, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{sv.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginBottom: 10 }}>
                      {sv.category ? sv.category.replace(/_/g, ' ') : ''}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => navigate(`/services/edit/${sv.id}`)}
                        style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button onClick={() => handleDeleteService(sv.id, sv.name)}
                        style={{ flex: 1, background: '#FBEAEA', color: '#B23B3B', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {(filter === 'all' || filter === 'skills') && skills.length > 0 && (
              <Section title="Skill Share" emoji="✨" count={skills.length}>
                {skills.map(sk => (
                  <EditableRow key={sk.id} item={sk} typeLabel="Skill Share"
                    onSave={updateSkillListing} onDelete={handleDeleteSkill}
                    extraFields={[
                      { key: 'contactEmail', placeholder: 'Your email' },
                      { key: 'contactPhone', placeholder: 'Your phone (optional)' },
                    ]} />
                ))}
              </Section>
            )}

            {(filter === 'all' || filter === 'notices') && notices.length > 0 && (
              <Section title="Noticeboard" emoji="🤝" count={notices.length}>
                {notices.map(no => (
                  <EditableRow key={no.id} item={no} typeLabel="Noticeboard"
                    onSave={updateNotice} onDelete={handleDeleteNotice}
                    extraFields={[
                      { key: 'email', placeholder: 'Your email' },
                      { key: 'phone', placeholder: 'Your phone (optional)' },
                    ]} />
                ))}
              </Section>
            )}

            {(filter === 'all' || filter === 'meetlocals') && meetLocals.length > 0 && (
              <Section title="Local Meet-ups" emoji="🤝" count={meetLocals.length}>
                {meetLocals.map(ml => (
                  <EditableRow key={ml.id} item={ml} typeLabel="Local Meet-up"
                    onSave={updateMeetLocal} onDelete={handleDeleteMeetLocal}
                    extraFields={[
                      { key: 'email', placeholder: 'Your email' },
                      { key: 'phone', placeholder: 'Your phone (optional)' },
                    ]} />
                ))}
              </Section>
            )}

            {(filter === 'all' || filter === 'deals') && deals.length > 0 && (
              <Section title="Deals" emoji="🏷️" count={deals.length}>
                {deals.map(de => (
                  <EditableRow key={de.id} item={de} typeLabel="Deal"
                    onSave={updateDeal} onDelete={handleDeleteDeal}
                    extraFields={[
                      { key: 'businessName', placeholder: 'Business name' },
                      { key: 'businessAddress', placeholder: 'Business address' },
                    ]}
                    secondaryLine={de.expiresAt ? `Expires ${fmtDate(de.expiresAt)}` : null} />
                ))}
              </Section>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default MyPostsPage;
